'use client';

import React, { useState, useRef } from 'react';
import { Plus, Crown } from 'lucide-react';
import { Guild } from '../../../types';
import { useDashboard } from '@/components/dashboard/DashboardContext';
import {
  Avatar,
  AvatarImage,
  AvatarFallback
} from '@/components/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

interface ServerItemProps {
  guild: Guild;
  isSelected: boolean;
  onClick: () => void;
  preserveDrawer?: boolean;
}

const ServerItem: React.FC<ServerItemProps> = ({ guild, isSelected, onClick, preserveDrawer = false }) => {
  const { closeSidebar } = useDashboard();
  const [popoverOpen, setPopoverOpen] = useState(false);

  // 클릭 핸들러
  const handleClick = () => {
    if (guild.has_kamibot === false) {
      setPopoverOpen(true);
    } else {
      onClick();
      if (!preserveDrawer) {
        closeSidebar();
      }
    }
  };

  // 팝오버 열림/닫힘 제어 핸들러
  const handlePopoverChange = (open: boolean) => {
    // 팝오버를 열려고 할 때만 카미봇 존재 여부 확인
    if (open) {
      // 카미봇이 없는 서버에서만 팝오버 열기 허용
      setPopoverOpen(guild.has_kamibot === false);
    } else {
      // 팝오버를 닫는 것은 항상 허용
      setPopoverOpen(false);
    }
  };

  // 서버 아이콘 URL 생성
  const getServerIconUrl = () => {
    if (!guild.icon) {
      return null;
    }
    return `https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`;
  };

  // 서버 약어 생성 (아이콘이 없을 때 표시)
  const getServerAcronym = () => {
    return guild.name
      .split(' ')
      .map(word => word[0])
      .join('')
      .substr(0, 2)
      .toUpperCase();
  };

  const iconUrl = getServerIconUrl();
  const hasKamibot = guild.has_kamibot !== false;

  return (
    <TooltipProvider>
      <Tooltip>
        <Popover open={popoverOpen} onOpenChange={handlePopoverChange}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <div 
                className={`relative cursor-pointer w-12 h-12 mb-1
                  ${isSelected ? 'rounded-[30%]' : 'rounded-full'} 
                  transition-all duration-200 hover:rounded-[30%]
                  ${isSelected ? 'ring-2 ring-[var(--selected-item)]' : ''}`}
                onClick={handleClick}
              >
                {isSelected && (
                  <div className="absolute left-[-8px] top-1/2 transform -translate-y-1/2 w-1 h-10 rounded-full bg-[var(--selected-item)]" />
                )}

                <Avatar 
                  className={`w-12 h-12
                    ${isSelected ? 'rounded-[30%]' : 'rounded-full'} 
                    transition-all duration-200 hover:rounded-[30%]
                    ${isSelected ? 'bg-[var(--selected-item)]' : 'bg-accent/50 hover:bg-[var(--hover-item)]'}
                    ${hasKamibot ? 'opacity-100' : 'opacity-60'}`}
                >
                  {iconUrl ? (
                    <AvatarImage src={iconUrl} alt={guild.name} />
                  ) : (
                    <AvatarFallback className={`
                      ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-accent/50'}`
                    }>
                      {getServerAcronym()}
                    </AvatarFallback>
                  )}
                </Avatar>

                {/* 카미봇이 없는 서버에는 플러스 아이콘 표시 */}
                {guild.has_kamibot === false && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-full">
                    <Plus className="h-8 w-8 opacity-50 text-foreground" />
                  </div>
                )}

                {/* 서버 소유자 표시 */}
                {guild.owner && (
                  <div className="absolute -bottom-0.5 -right-0.5 bg-secondary text-secondary-foreground rounded-full p-0.5 border-2 border-background">
                    <Crown className="h-3 w-3" />
                  </div>
                )}
              </div>
            </PopoverTrigger>
          </TooltipTrigger>

          <PopoverContent side="right" align="center" className="w-[250px] p-4">
            <div className="space-y-4">
              <h4 className="font-medium text-center">{guild.name} 서버에 카미봇을 추가할까요?</h4>
              <Button
                variant="default"
                className="w-full"
                asChild
              >
                <a
                  href={`https://discord.com/oauth2/authorize?client_id=1019061779357245521&permissions=3149051&scope=bot%20applications.commands&guild_id=${guild.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  추가하기
                </a>
              </Button>
            </div>
          </PopoverContent>
          
          <TooltipContent side="right">
            {guild.name}
          </TooltipContent>
        </Popover>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ServerItem;
